import {
    Add,
    Archive,
    FoodBank,
    MoneyOffCsred,
    ShoppingCartCheckout,
    Inventory,
    SupportAgent,
    Stars,
    Language,
    AccountBalanceWallet,
} from "@mui/icons-material";
import {
    Badge,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon,
} from "@mui/material";
import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../AppContext";
import { PosContext } from "../shared/PosContext";
import { getTakeawayUrlSegment } from "../utils/floorUtils";
import { isRoleAdmin, isRoleService } from "../frappeConfig";
import CashOutDialog from "../pages/PosPage/components/CashOutDialog/CashOutDialog";
import { useTranslation } from "react-i18next";

import CustomerBalanceManager, { canManageBalances } from "./CustomerBalanceManager";

const SimpleSpeedDialActions = ({
    handleTakeawayOrders,
    handleClosedOrders,
    handleClosePOSCash,
    handleCallCenterOrders,
    handleLoyaltySidebar,
    notifications,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        userInfo,
        isTabsPos,
        isRetailPos,
        hideClosedOrders,
        workMode,
        posProfile,
        restaurantSettings,
    } = useContext(AppContext);
    const { takeawayTable } = useContext(PosContext);
    const isServiceUser = isRoleService(userInfo?.role_profile_name);

    const takeawayPathSegment = useMemo(
        () => getTakeawayUrlSegment(posProfile, takeawayTable),
        [posProfile, takeawayTable]
    );

    const [balancesOpen, setBalancesOpen] = useState(false);
    const balanceActions = canManageBalances(restaurantSettings?.docs?.[0], userInfo?.role_profile_name) ? [{label:t("customerBalances.title"),icon:<AccountBalanceWallet/>,onClick:()=>setBalancesOpen(true)}] : [];
    const [closePOSDialogOpen, setClosePOSDialogOpen] = useState(false);

    const closedOrdersActions = hideClosedOrders
        ? []
        : [
              {
                  label: t("speedDial.closedOrders"),
                  icon: <Archive />,
                  onClick: () => {
                      handleClosedOrders?.();
                  },
              },
          ];

    const onlineOrderActions =
        !isRetailPos &&
        Number(restaurantSettings?.docs?.[0]?.restaurant_storefront_enabled) === 1
            ? [
                  {
                      label: "Online orders",
                      icon: <Language />,
                      onClick: () => navigate("/restaurant-orders"),
                  },
              ]
            : [];

    const handleExpenseClaimClose = () => {
        setClosePOSDialogOpen(false);
    };

    const onConfirmExpenseClaim = () => {
        setClosePOSDialogOpen(false);
    };

    const speedDialActions = () => {
        if (isRetailPos) {
            if (isServiceUser) return [];

            return [
                {
                    label: t("speedDial.loyalty"),
                    icon: <Stars />,
                    onClick: () => {
                        handleLoyaltySidebar?.();
                    },
                },
                {
                    label: t("speedDial.cashOut"),
                    icon: <ShoppingCartCheckout />,
                    onClick: () => {
                        setClosePOSDialogOpen(true);
                    },
                },
                ...closedOrdersActions,
                {
                    label: t("speedDial.closePosCash"),
                    icon: <MoneyOffCsred />,
                    onClick: () => {
                        handleClosePOSCash?.();
                    },
                },
            ];
        }

        if (isTabsPos) {
            if (isServiceUser) {
                return [
                    {
                        label: t("speedDial.newOrder"),
                        icon: <Add />,
                        onClick: () => {
                            navigate(`/pos/${takeawayPathSegment}`);
                        },
                    },
                ];
            }

            const allowedActions = [];

            if (isRoleAdmin(userInfo.role_profile_name)) {
                allowedActions.push({
                    label: t("speedDial.items"),
                    icon: <Inventory />,
                    onClick: () => {
                        navigate("/items");
                    },
                });
            }

            return [
                {
                    label: t("speedDial.newOrder"),
                    icon: <Add />,
                    onClick: () => {
                        navigate(`/pos/${takeawayPathSegment}`);
                    },
                },
                ...onlineOrderActions,
                ...allowedActions,
                {
                    label: t("speedDial.loyalty"),
                    icon: <Stars />,
                    onClick: () => {
                        handleLoyaltySidebar?.();
                    },
                },
                {
                    label: t("speedDial.cashOut"),
                    icon: <ShoppingCartCheckout />,
                    onClick: () => {
                        setClosePOSDialogOpen(true);
                    },
                },
                ...closedOrdersActions,
                {
                    label: t("speedDial.closePosCash"),
                    icon: <MoneyOffCsred />,
                    onClick: () => {
                        handleClosePOSCash?.();
                    },
                },
            ];
        } else if (isRoleService(userInfo.role_profile_name)) {
            return [
                ...onlineOrderActions,
                {
                    label: t("speedDial.loyalty"),
                    icon: <Stars />,
                    onClick: () => {
                        handleLoyaltySidebar?.();
                    },
                },
                {
                    label: t("speedDial.takeawayOrders"),
                    icon: <FoodBank />,
                    onClick: () => {
                        handleTakeawayOrders?.();
                    },
                },
            ];
        } else {
            const allowedActions = [];

            if (isRoleAdmin(userInfo.role_profile_name)) {
                allowedActions.push({
                    label: t("speedDial.items"),
                    icon: <Inventory />,
                    onClick: () => {
                        navigate("/items");
                    },
                });
            }

            allowedActions.push(
                ...[
                    ...onlineOrderActions,
                    ...(workMode !== "call_center"
                        ? [
                              {
                                  label: t("speedDial.callCenterOrders"),
                                  icon: <SupportAgent />,
                                  onClick: () => {
                                      handleCallCenterOrders?.();
                                  },
                              },
                          ]
                        : []),
                    {
                        label: t("speedDial.loyalty"),
                        icon: <Stars />,
                        onClick: () => {
                            handleLoyaltySidebar?.();
                        },
                    },
                    {
                        label: t("speedDial.takeawayOrders"),
                        icon: <FoodBank />,
                        onClick: () => {
                            handleTakeawayOrders?.();
                        },
                    },
                    {
                        label: t("speedDial.cashOut"),
                        icon: <ShoppingCartCheckout />,
                        onClick: () => {
                            setClosePOSDialogOpen(true);
                        },
                    },
                    ...closedOrdersActions,
                    {
                        label: t("speedDial.closePosCash"),
                        icon: <MoneyOffCsred />,
                        onClick: () => {
                            handleClosePOSCash?.();
                        },
                    },
                ]
            );
            return allowedActions;
        }
    };

    return (
        <React.Fragment>
            <CustomerBalanceManager open={balancesOpen} onClose={()=>setBalancesOpen(false)} posProfile={posProfile?.name}/>
            <SpeedDial
                icon={
                    <Badge
                        color="secondary"
                        badgeContent={notifications?.openingEntries}
                    >
                        <SpeedDialIcon />
                    </Badge>
                }
                ariaLabel={t("speedDial.ariaLabel")}
                sx={{
                    position: "absolute",
                    bottom: 15,
                    right: 15,
                    "& .MuiSpeedDial-actions": {
                        gap: "12px", // Increase spacing between actions
                    },
                    "& .MuiSpeedDialAction-staticTooltipLabel": {
                        whiteSpace: "nowrap", // Prevent text wrapping
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        // backgroundColor: 'rgba(97, 97, 97, 0.92)',
                        borderRadius: "4px",
                        padding: "2px 8px",
                    },
                }}
            >
                {[...(speedDialActions() || []), ...balanceActions].map((action) => (
                    <SpeedDialAction
                        key={"Speed_Dial_" + action.label}
                        icon={action.icon}
                        aria-label={action.label}
                        tooltipTitle={action.label}
                        tooltipOpen
                        onClick={action.onClick}
                        sx={{
                            margin: "4px 0", // Additional spacing between actions
                        }}
                    />
                ))}
            </SpeedDial>

            <CashOutDialog
                dialogOpen={closePOSDialogOpen}
                onCancel={handleExpenseClaimClose}
                onConfirm={onConfirmExpenseClaim}
                // selectedCustomer={selectedCustomer}
                // customerSelected={onCustomerSelected}
            ></CashOutDialog>
        </React.Fragment>
    );
};

export default SimpleSpeedDialActions;
